import type { TelemetryPropertyValueType } from '@ms/yammer-telemetry-support';

import type { AllowedInfoEventPropertyNames, InfoEvent, InfoEventName } from '../events';
import { emitTelemetryEvent, emitTelemetryEventNow } from '../report/eventSink';

import { filterUnsafeProperties } from './eventPropertyCheck';

interface ReportInfoOptions {
  /**
   * The event's name as defined in InfoEventName. This will be logged in the event's
   * 'EventName' column in the 'yamjs' table.
   */
  readonly eventName: InfoEventName;
  /**
   * An optional set of custom properties to log for the event. These property names must be
   * defined in the AllowedInfoEventPropertyNames type.
   * (see the README about the process for adding new custom properties). Each value passed here will
   * be logged into a property-named column in the 'yamjs' table. There may be extra work involved to
   * add a property column if it does not already exist in the 'yamjs' table (see README).
   */
  readonly eventProperties?: Partial<Record<AllowedInfoEventPropertyNames, TelemetryPropertyValueType>>;
  /**
   * Whether to skip authentication and use a fallback token for reporting this event.
   * This should be set to true when reporting authentication-related events to avoid circular dependencies.
   * Defaults to false.
   */
  readonly skipAuthentication?: boolean;
}

const getInfoEvent = (options: ReportInfoOptions): InfoEvent => {
  const { eventName, eventProperties = {}, skipAuthentication } = options;

  return {
    type: 'Info',
    name: eventName,
    occurredAt: Date.now().toString(),
    skipAuthentication,
    properties: filterUnsafeProperties(eventName, eventProperties),
  };
};

type ReportInfo = (options: ReportInfoOptions) => void;
export const reportInfo: ReportInfo = (options) => {
  const infoEvent = getInfoEvent(options);
  void emitTelemetryEvent(infoEvent);
};

type ReportInfoNow = (options: ReportInfoOptions) => Promise<void>;
export const reportInfoNow: ReportInfoNow = async (options) => {
  const infoEvent = getInfoEvent(options);
  await emitTelemetryEventNow(infoEvent);
};
